import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {Product} from "../../../../model/product";
import {Document} from "../../../../model/document";
import {ConfirmationDialogComponent} from "../../../dialogs/confirmation-dialog/confirmation-dialog.component";
import {ModalComponent} from "../../../modal/modal.component";
import NameFinderUtils from "../../../../utils/name-finder-utils";
import {BookableService} from "../../../../model/bookableService";
import {ServiceCategory} from "../../../../model/serviceCategory";
import {Service} from "../../../../model/service";
import {ProductService} from "../../../../service/product.service";
import {DocumentClass} from "../../../../model/documentClass";
import {SubClass} from "../../../../model/subClass";
import {SubInformation} from "../../../../model/subInformation";
import {Property} from "../../../../model/property";
import {LandregisterfolioUtils} from "../../../../utils/LandregisterfolioUtils";
import ProductUtils from "../../../../utils/product-utils";
import {ObjectOwnerComponent} from "../../../object-owner/object-owner.component";
import {FolioNumberComponent} from "../../../folio-number/folio-number.component";
import {ObjectOwner} from "../../../../model/object-owner";
import {merge} from "rxjs";
import {CustomValidators} from "../../../../CustomValidators";
import FormUtils from "../../../../utils/form-utils";
import {AdditionalConstructionFileData} from "../../../../model/additionalConstructionFileData";
import {User} from "../../../../model/user";
import {PricingUtils} from "../../../../utils/pricing-utils";

@Component({
  selector: 'app-bauak-modal',
  templateUrl: './bauak-modal.component.html',
  styleUrls: ['./bauak-modal.component.css']
})
export class BauakModalComponent implements OnInit, OnChanges{

  @Input() active: boolean = false;
  @Input() user: User;
  @Input() documents: Document[] = [];
  @Input() bauakProducts: Product[] = [];
  @Input() isAllowedToOrder: boolean = false;
  @Input() bookableServicesByCategory: Map<string, BookableService[]> = new Map<string, BookableService[]>();
  @Input() serviceCategories: ServiceCategory[] = [];
  // Additional inputs for step 2 (Grunde)
  @Input() products: Product[] = [];
  @Input() selectedProperty: Property;
  @Input() isConnectOrigin: boolean = false;
  @Input() startOnGrundeStep: boolean = false;

  @Output() modalClosed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<Document[]>();
  @Output() removeRequested = new EventEmitter<void>();

  @ViewChild('modalRef') modalRef: ModalComponent;
  @ViewChild(ObjectOwnerComponent) objectOwnerComponent: ObjectOwnerComponent;
  @ViewChild(FolioNumberComponent) folioNumberComponent: FolioNumberComponent;

  bauaktenForm: UntypedFormGroup;
  maxBauakInfoInput = 1000;
  selectedCService: string;
  selectedAdditionalBauakServices: string[] = [];
  showInvalidInputs: boolean = false;
  getCategoryLongName = NameFinderUtils.getCategoryLongName;
  // Bauakte subclasses
  bauakDocumentClass: DocumentClass;
  bauakSubClasses: SubClass[] = [];
  sonstigesKey: string = 'sonst';
  pbauakKey: string = 'pbauak';

  bauakBasePrice: number;
  bauakPricePerHour: number;

  // Multi-step flow
  step: 1 | 2 = 1;
  private pendingBauakDocs: Document[] = [];

  protected readonly ProductUtils = ProductUtils;
  protected readonly PricingUtils = PricingUtils;

  constructor(private readonly formBuilder: UntypedFormBuilder,
              private readonly dialog: MatDialog,
              private readonly productService: ProductService) {}

  /**
   * Initializes the reactive form, registers dynamic controls and validators,
   * subscribes to selection changes, and pre-fills values when applicable.
   */
  ngOnInit(): void {

    this.bauaktenForm = this.formBuilder.group({
      bauakSelections: this.formBuilder.group({}),
      cService: new UntypedFormControl('', Validators.required),
      abServices: this.formBuilder.group({}),
      bauakSubclasses: this.formBuilder.group({}),
      constructionYear: new UntypedFormControl(null),
      additionalInfo: new UntypedFormControl(null, Validators.maxLength(this.maxBauakInfoInput)),
      partDescription: new UntypedFormControl(null, Validators.maxLength(this.maxBauakInfoInput)),
    })

    this.bauakBasePrice = PricingUtils.getBauakBasePrice();
    this.bauakPricePerHour = PricingUtils.getBauakPricePerHour();

    // Initialize ABServices checkbox controls if provided
    const abServices = this.bookableServicesByCategory?.get?.('ABServices') || [];
    const abServicesGroup = this.bauaktenForm.get('abServices') as UntypedFormGroup;
    abServices.forEach(service => abServicesGroup.addControl(service.shortName, new UntypedFormControl(false)));

    // Initialize selection checkboxes for products
    this.initSelectionControls();
    // Initialize Bauakte subclasses including checkboxes
    this.initBauakDocumentClass();
    // Handle selection changes to enforce exclusivity and validators
    this.handleSelectionChange();
    // Pre-fill if an existing bauakten document is found
    this.updateValuesForExistingBauaktenDocument();
  }

  /**
   * Handles input changes and pre-fills the form when the modal becomes active.
   *
   * @param {SimpleChanges} changes The Angular changes object for input bindings.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const opened = changes['active']?.currentValue === true && changes['active']?.previousValue !== true;
    if (opened) {
      // Pre-fill values from existing docs
      this.updateValuesForExistingBauaktenDocument();
      this.initGrundeStepIfApplicable();
    }
  }

  /**
   * Indicates whether the optional Grunde step should be shown (step 2).
   *
   * @return {boolean} True if Grunde is required and not already selected.
   */
  public get hasGrundeStep(): boolean {
    return !!this.grundeShortName && !this.hasExistingGrunde && !this.isConnectOrigin;
  }

  /**
   * Returns Bauak-related products sorted by display/processing priority.
   *
   * @return {Product[]} The sorted list of Bauak products.
   */
  public sortedBauakProducts(): Product[] {
    return [...(this.bauakProducts || [])].sort((a, b) => this.getBauakPriority(a) - this.getBauakPriority(b));
  }

  /**
   * Checks whether the 'Teile aus der Bauakte' (pbauak) option is selected.
   *
   * @return {boolean} True if pbauak is currently selected.
   */
  public isBauakPartSelected(): boolean{
    return !!(this.pbauakKey && (this.bauaktenForm.get('bauakSelections') as UntypedFormGroup)?.get(this.pbauakKey)?.value);
  }

  /**
   * Tries to find an existing Bauakten document in the current cart/documents list.
   * Prefers the combined document (bauakms) if present.
   *
   * @return {Document|null} The found document or null if none is present.
   */
  public findBauaktenDocument(): Document | null {
    const documents = this.documents || [];

    // Prefer combined document if it exists
    const combined = documents.find(d => d?.docname?.toLowerCase() === 'bauakms');
    if (combined) {
      return combined;
    }

    // Otherwise find any Bauakten-related single document
    return documents.find(d => this.bauakProducts.some(product => d?.docname === product.nameShort)) ?? null;
  }

  /**
   * Opens a confirmation dialog and, upon confirmation, requests removal of
   * Bauakten documents from the parent component and closes the modal.
   */
  public removeBauaktenDocument(): void{
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: 'auto',
      data: {
        title: 'Daten löschen?',
        content: 'Sind Sie sicher, dass die Bauakten aus dem Warenkorb entfernt werden sollen? Alle Angaben gehen verloren.'
      }
    })

    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.removeRequested.emit();
        this.modalRef?.closeModal();
      }
    })
  }

  /**
   * Validates the form, constructs the resulting document(s) depending on the
   * current selections, and either completes submission or advances to step 2.
   */
  public onSubmit(): void {
    if (!this.bauaktenForm.valid) {
      this.invalidateForm();
      return;
    }

    // Extract common form values
    const info = this.bauaktenForm.value.additionalInfo as string;
    const partDescription = this.bauaktenForm.value.partDescription as string;
    const constructionYear = this.bauaktenForm.value.constructionYear as string;
    const cService = this.bauaktenForm.value.cService as string;

    const abServices = this.extractCheckedKeys('abServices');
    const selectedKeys = this.extractCheckedKeys('bauakSelections');

    // Require at least one selection + a cService
    if (selectedKeys.length === 0 || !cService) {
      this.invalidateForm();
      return;
    }

    // Combine all services
    const servicesToAdd = [...abServices, cService];

    // Build the list of result documents based on selection
    this.pendingBauakDocs = this.buildResultDocuments(selectedKeys, info, partDescription, constructionYear, servicesToAdd);

    this.showInvalidInputs = false;

    // If no "Grunde" product needed or already present → finish directly
    if (!this.grundeShortName || this.hasExistingGrunde || this.isConnectOrigin) {
      this.finishCombinedSubmit();
      return;
    }

    // Otherwise continue to step 2 (Grunde UI)
    this.step = 2;
    this.modalRef?.scrollToTop();
  }

  /**
   * Navigates back to step 1.
   */
  public backToStepOne(): void{
    this.step = 1;
  }

  /**
   * Validates Grunde-related child forms, persists owners and folio numbers
   * into the selected property and finishes the overall submission.
   */
  public onConfirmGrunde(): void {
    // Validate child forms before proceeding
    const isObjectOwnerFormValid = this.objectOwnerComponent?.isObjectOwnerFormValid();
    const isFolioNumberFormValid = this.folioNumberComponent?.isFolioNumberFormValid();
    if (!isObjectOwnerFormValid || !isFolioNumberFormValid) {
      return;
    }

    if (this.selectedProperty) {
      // Persist owners
      if (this.objectOwnerComponent) {
        this.selectedProperty.objectOwnerList = this.mapObjectOwners();
      }

      // Persist folio numbers
      if (this.folioNumberComponent) {
        this.persistFolioData();
      }
    }

    this.finishCombinedSubmit();
  }

  /**
   * Emits all pending documents (including optional Grunde), resets temporary
   * state and closes the modal.
   */
  private finishCombinedSubmit(): void{
    const docs: Document[] = [];
    // include pending Bauak docs
    docs.push(...(this.pendingBauakDocs || []));
    // add Grunde document if available
    if(this.grundeShortName && !this.hasExistingGrunde){
      const grundeDocument = new Document(this.grundeShortName);
      docs.push(grundeDocument);
    }
    // Emit to parent and close
    this.submitted.emit(docs);
    this.pendingBauakDocs = [];
    this.step = 1;
    this.modalRef?.closeModal();
  }

  /**
   * Conditionally initializes the workflow on the 'grunde' step.
   *
   * If the configuration allows starting on the Grunde step and such a step
   * exists, this method checks for already existing Bauak documents.
   * When found, those documents are marked as pending and the UI is advanced
   * directly to step 2.
   */
  private initGrundeStepIfApplicable(): void {
    if (!this.startOnGrundeStep || !this.hasGrundeStep) {
      return;
    }

    const existingBauakDocs = this.getExistingBauakDocuments();
    if (!existingBauakDocs.length) {
      return;
    }

    this.pendingBauakDocs = existingBauakDocs;
    this.step = 2;
    this.modalRef?.scrollToTop();
  }

  /**
   * Emits a signal when the modal is closed so the parent can react.
   */
  public onModalClosed(): void{
    this.modalClosed.emit();
  }

  /**
   * Resets and restores the form state from already selected Bauakten-related
   * documents in the cart (including combined and Teil cases).
   */
  public updateValuesForExistingBauaktenDocument(): void {
    // Reset global state and form controls
    this.resetState();

    const docs = this.documents || [];
    const products = this.bauakProducts || [];

    // Resolve product keys
    const statikKey = products.find(p => (p?.nameShort || '').toLowerCase().includes('statik'))?.nameShort;
    const bauakteKey = products.find(p => {
      const shortName = (p?.nameShort || '').toLowerCase();
      return !shortName.includes('statik') && !shortName.includes('pbauak') && shortName.includes('bauak');
    })?.nameShort;

    // Case 1: restore "Teile aus der Bauakte" document if present
    const teilDoc = this.findTeilDocument(docs);
    if (teilDoc) {
      this.restoreTeilDocument(teilDoc);
      this.updateValidators(true);
      return;
    }

    // Case 2: restore Bauakte/Statik or combined document
    this.restoreBauakteOrStatik(docs, bauakteKey, statikKey);

    // Restore services from related docs
    this.restoreServices(docs, bauakteKey, statikKey);

    // Restore additional info and construction year from related docs
    this.restoreAdditionalDataFromDocs(docs, bauakteKey, statikKey);

    // Ensure validators for non-Teil case
    this.updateValidators(false);
  }

  /**
   * Clears internal selection state and resets all related form controls.
   */
  private resetState(): void {
    this.selectedCService = undefined;
    this.selectedAdditionalBauakServices = [];
    this.showInvalidInputs = false;
    this.step = 1;

    const selGroup = this.bauaktenForm.get('bauakSelections') as UntypedFormGroup;
    const abGroup = this.bauaktenForm.get('abServices') as UntypedFormGroup;
    const subGroup = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup;

    [selGroup, abGroup, subGroup].forEach(group => {
      if (group) {
        Object.keys(group.controls).forEach(key => group.get(key)?.setValue(false, { emitEvent: false }));
      }
    });

    this.bauaktenForm.patchValue({ cService: '' }, { emitEvent: false });
    this.bauaktenForm.patchValue({ partDescription: null }, { emitEvent: false });
    this.bauaktenForm.patchValue({ additionalInfo: null }, { emitEvent: false });
    this.bauaktenForm.patchValue({ constructionYear: null }, { emitEvent: false });
  }

  /**
   * Tries to extract all 'Bauakten' documents from this.documents
   * @return Arary of all 'Bauakten' documents
   */
  private getExistingBauakDocuments(): Document[] {
    const docs = this.documents || [];
    return docs.filter(document =>
      ProductUtils.BAUAK_DOCUMENT_NAMES.includes((document?.docname || '').toLowerCase())
    );
  }

  /**
   * Finds a 'Teile aus der Bauakte' document among the given documents.
   *
   * @param {Document[]} docs The list of documents to search in.
   * @return {Document|null} The Teil document or null if none was found.
   */
  private findTeilDocument(docs: Document[]): Document | null {
    return docs.find(d => this.pbauakKey && d.docname === this.pbauakKey) ?? null;
  }

  /**
   * Restores form selections, subclasses, additional data and services from a
   * Teil (pbauak) document.
   *
   * @param {Document} teilDoc The Teil document to restore from.
   */
  private restoreTeilDocument(teilDoc: Document): void {
    const selGroup = this.bauaktenForm.get('bauakSelections') as UntypedFormGroup;
    const abGroup = this.bauaktenForm.get('abServices') as UntypedFormGroup;
    const subGroup = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup;

    // Restore selection
    selGroup?.get(this.pbauakKey!)?.setValue(true, { emitEvent: false });

    // Restore subclasses
    teilDoc.subInformations?.forEach(si => {
      if (si?.nameShort && subGroup?.get(si.nameShort)) {
        subGroup.get(si.nameShort)?.setValue(true, { emitEvent: false });
      }
    });

    // Restore additional data
    const additionalConstructionFileData = teilDoc.additionalConstructionFileData;
    this.bauaktenForm.patchValue({
      partDescription: additionalConstructionFileData?.partDescription ??  null,
      additionalInfo: additionalConstructionFileData?.additionalInfo ?? null,
      constructionYear: additionalConstructionFileData?.constructionYear ?? null,
    }, { emitEvent: false });

    // Restore services
    teilDoc.selectedServices?.forEach(s => {
      const category = NameFinderUtils.findBookableServiceCategoryByShortName(s.nameShort, this.bookableServicesByCategory);

      if (category === 'CService') {
        this.selectedCService = s.nameShort;
        this.bauaktenForm.patchValue({ cService: s.nameShort }, { emitEvent: false });
      }

      if (category === 'ABServices' && abGroup?.get(s.nameShort)) {
        abGroup.get(s.nameShort)?.setValue(true, { emitEvent: false });
        this.selectedAdditionalBauakServices.push(s.nameShort);
      }
    });
  }

  /**
   * Restores main selection checkboxes for Bauakte and/or Statik (or combined)
   * based on available documents.
   *
   * @param {Document[]} docs The list of documents to inspect.
   * @param {string} [bauakteKey] The product short name for Bauakte.
   * @param {string} [statikKey] The product short name for Statik.
   */
  private restoreBauakteOrStatik(docs: Document[], bauakteKey?: string, statikKey?: string): void {
    const selGroup = this.bauaktenForm.get('bauakSelections') as UntypedFormGroup;
    const isCombined = docs.some(d => (d?.docname || '').toLowerCase() === 'bauakms');

    const hasBauakte = isCombined || docs.some(d => bauakteKey && d.docname === bauakteKey);
    const hasStatik = isCombined || docs.some(d => statikKey && d.docname === statikKey);

    if (hasBauakte && bauakteKey) {
      selGroup?.get(bauakteKey)?.setValue(true, { emitEvent: false });
    }
    if (hasStatik && statikKey) {
      selGroup?.get(statikKey)?.setValue(true, { emitEvent: false });
    }
  }

  /**
   * Restores general additional data (info, construction year) from the most
   * relevant related document (combined > Bauakte > Statik).
   *
   * @param {Document[]} docs The list of documents to inspect.
   * @param {string} [bauakteKey] The product short name for Bauakte.
   * @param {string} [statikKey] The product short name for Statik.
   */
  private restoreAdditionalDataFromDocs(docs: Document[], bauakteKey?: string, statikKey?: string): void {
    const combinedDoc = docs.find(d => (d?.docname || '').toLowerCase() === 'bauakms');
    const bauakteDoc = docs.find(d => bauakteKey && d.docname === bauakteKey);
    const statikDoc = docs.find(d => statikKey && d.docname === statikKey);

    const preferredDoc = combinedDoc || bauakteDoc || statikDoc;
    if (!preferredDoc) {
      return;
    }

    const additionalConstructionFileData = preferredDoc.additionalConstructionFileData;
    this.bauaktenForm.patchValue({
      additionalInfo: additionalConstructionFileData?.additionalInfo ??  null,
      constructionYear: additionalConstructionFileData?.constructionYear ?? null,
    }, { emitEvent: false });
  }

  /**
   * Restores selected services (CService and ABServices) from any related
   * document (combined, Bauakte, or Statik).
   *
   * @param {Document[]} docs The list of documents to inspect.
   * @param {string} [bauakteKey] The product short name for Bauakte.
   * @param {string} [statikKey] The product short name for Statik.
   */
  private restoreServices(docs: Document[], bauakteKey?: string, statikKey?: string): void {
    const abGroup = this.bauaktenForm.get('abServices') as UntypedFormGroup;

    const relatedDocs = docs.filter(d =>
        (bauakteKey && d.docname === bauakteKey) ||
        (statikKey && d.docname === statikKey) ||
        ((d.docname || '').toLowerCase() === 'bauakms')
    );

    relatedDocs.forEach(doc => {
      doc.selectedServices?.forEach(s => {
        const category = NameFinderUtils.findBookableServiceCategoryByShortName(s.nameShort, this.bookableServicesByCategory);

        if (category === 'CService' && !this.selectedCService) {
          this.selectedCService = s.nameShort;
          this.bauaktenForm.patchValue({ cService: s.nameShort }, { emitEvent: false });
        }

        if (category === 'ABServices' && abGroup?.get(s.nameShort)) {
          abGroup.get(s.nameShort)?.setValue(true, { emitEvent: false });
          if (!this.selectedAdditionalBauakServices.includes(s.nameShort)) {
            this.selectedAdditionalBauakServices.push(s.nameShort);
          }
        }
      });
    });
  }

  /**
   * Subscribes to selection control changes and enforces mutual exclusivity
   * between options as well as context-sensitive validators.
   */
  private handleSelectionChange(): void{
    const selections = this.bauaktenForm.get('bauakSelections') as UntypedFormGroup;

    const bauakSelection$  = selections.get('bauak')?.valueChanges;
    const statikSelection$ = selections.get('statik')?.valueChanges;
    const pbauakSelection$ = selections.get('pbauak')?.valueChanges;

    // React to selection changes to enforce exclusivity and validators
    merge(bauakSelection$, statikSelection$).subscribe(() => {
      this.bauaktenForm.get('bauakSelections').get('pbauak').setValue(false, {emitEvent:false});
      this.clearBauakPartFields();
    });

    pbauakSelection$.subscribe((selected: boolean) => {
      // Enforce exclusivity
      this.bauaktenForm.get('bauakSelections').get('bauak').setValue(false, {emitEvent:false});
      this.bauaktenForm.get('bauakSelections').get('statik').setValue(false, {emitEvent:false});

      // Update validators for subclasses and part description
      this.updateValidators(!!selected);

      // If pbauak was turned off manually, clear related fields
      if (!selected) {
        this.clearBauakPartFields();
      }
    });
  }

  /**
   * Computes a sorting priority for a Bauak product based on its short name.
   *
   * @param {Product} product The product to evaluate.
   * @return {number} Lower numbers indicate higher priority.
   */
  private getBauakPriority(product: Product): number {
    const shortName = product?.nameShort?.toLowerCase() ?? '';

    const priorityRules: { check: (name: string) => boolean; priority: number }[] = [
      { check: name => name.includes('pbauak'), priority: 3 },
      { check: name => name.includes('statik'), priority: 2 },
      { check: name => name.startsWith('bauak'), priority: 1 }
    ];

    const matchedRule = priorityRules.find(rule => rule.check(shortName));
    return matchedRule ? matchedRule.priority : 99;
  }

  /**
   * Dynamically adds selection checkbox controls for all Bauak products and
   * sets a validator requiring at least one selection.
   */
  private initSelectionControls(): void{
    const selectionGroup = this.bauaktenForm.get('bauakSelections') as UntypedFormGroup;
    if(!selectionGroup){
      return;
    }

    // Add a control per bauak product
    (this.bauakProducts || []).forEach(p => {
      if(!selectionGroup.get(p.nameShort)){
        selectionGroup.addControl(p.nameShort, new UntypedFormControl(false));
      }
    });

    // At least one of the three must be selected
    selectionGroup.setValidators([(group: UntypedFormGroup) => {
      const anySelected = Object.values(group.controls).some((ctrl: UntypedFormControl) => !!ctrl.value);
      return anySelected ? null : { required: true };
    }]);
  }

  /**
   * Initializes the Bauakte document class and its subclasses, loading from
   * session storage if present or fetching from the service otherwise.
   */
  private initBauakDocumentClass(): void {
    // If already present in the session storage use these values
    const cached = sessionStorage.getItem('PBAUAK');
    if(cached){
      this.bauakDocumentClass = JSON.parse(cached) as DocumentClass;
      this.bauakSubClasses = [...this.filterOutPbauakSubClasses(this.bauakDocumentClass?.subClasses || [])];
      this.addBauakSubClassControls();
      return;
    }

    // If not already in session storage fetch sub classes and add controls
    this.productService.getDocumentClassByShortname('PBAUAK').subscribe(documentClass => {
      this.bauakDocumentClass = documentClass;
      this.bauakSubClasses = [...this.filterOutPbauakSubClasses(documentClass?.subClasses || [])];
      this.addBauakSubClassControls();
      sessionStorage.setItem('PBAUAK', JSON.stringify(documentClass));
    });
  }

  /**
   * Creates a checkbox control for every Bauakte subclass and wires up the
   * 'Sonstiges' toggle to update the part description requirement.
   */
  private addBauakSubClassControls(): void{
    const group = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup;
    if(!group){
      return;
    }

    // Add a control per bauak subclass
    this.bauakSubClasses?.forEach(sc => {
      if(!group.get(sc.className)){
        group.addControl(sc.className, new UntypedFormControl(false));
      }
    });

    // Subscribe to 'Sonstiges' toggle (case-insensitive) to adjust part description requirement
    const sonstigesControlKey = Object.keys(group.controls)
      .find(k => (k || '').toLowerCase() === (this.sonstigesKey || '').toLowerCase());
    if (sonstigesControlKey) {
      group.get(sonstigesControlKey).valueChanges.subscribe((checked: boolean) => {
        const bauakPartSelected = this.isBauakPartSelected();
        this.togglePartDescriptionRequired(bauakPartSelected && !!checked);
        // Ensure validator changes take effect immediately for highlighting
        this.bauaktenForm.controls['partDescription'].updateValueAndValidity({ emitEvent: false });
      });
    }
  }

  /**
   * Clears subclass selections and resets the part description, also updating
   * validators to reflect a non-Teil state.
   */
  private clearBauakPartFields(): void{
    // Clear subclasses and part description for 'Teile aus der Bauakte'
    const subGroup = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup;
    if(subGroup){
      Object.keys(subGroup.controls).forEach(key => subGroup.get(key)?.setValue(false, {emitEvent:false}));
    }
    this.bauaktenForm.patchValue({ partDescription: null }, {emitEvent:false});
    // Update validators for non-Teil state
    this.updateValidators(false);
  }

  /**
   * Updates validators depending on whether 'Teile aus der Bauakte' is
   * selected and whether 'Sonstiges' is checked.
   *
   * @param {boolean} isTeilSelected True if the Teil option is selected.
   */
  private updateValidators(isTeilSelected: boolean): void {
    const partDescCtrl = this.bauaktenForm.controls['partDescription'];
    const subGroup = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup | null;
    if (subGroup) {
      // Require at least one subclass when 'Teile aus der Bauakte' is selected
      subGroup.setValidators(isTeilSelected ? [CustomValidators.atLeastOneSelectedValidator] : null);
      subGroup.updateValueAndValidity({ emitEvent: false });

      // Require part description only if 'Sonstiges' is checked while 'Teile' is selected
      const sonstigesControlKey = Object.keys(subGroup.controls)
        .find(k => (k || '').toLowerCase() === (this.sonstigesKey || '').toLowerCase());
      const sonstChecked = !!(sonstigesControlKey && subGroup.get(sonstigesControlKey)?.value);
      this.togglePartDescriptionRequired(isTeilSelected && sonstChecked);
    } else {
      this.togglePartDescriptionRequired(false);
    }
    partDescCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Convenience helper to check if the 'Sonstiges' subclass is selected.
   *
   * @return {boolean} True if 'Sonstiges' is selected.
   */
  public isSonstigesSelected(): boolean {
    const group = this.bauaktenForm.get('bauakSubclasses') as UntypedFormGroup;
    if (!group){
      return false;
    }
    const key = Object.keys(group.controls)
      .find(k => (k || '').toLowerCase() === (this.sonstigesKey || '').toLowerCase());
    return !!(key && group.get(key)?.value);
  }

  /**
   * Adds or removes Validators.required for the part description field.
   *
   * @param {boolean} required Whether the field should be required.
   */
  private togglePartDescriptionRequired(required: boolean): void{
    const partDescriptionControl = this.bauaktenForm.controls['partDescription'];
    if(required){
      partDescriptionControl.addValidators([Validators.required, Validators.maxLength(this.maxBauakInfoInput)]);
    } else {
      partDescriptionControl.clearValidators();
      partDescriptionControl.addValidators([Validators.maxLength(this.maxBauakInfoInput)]);
    }
  }

  /**
   * Returns the keys of controls that are checked within a given FormGroup.
   *
   * @param {string} controlName The name of the FormGroup control.
   * @return {string[]} The list of checked control keys.
   */
  private extractCheckedKeys(controlName: string): string[] {
    const group = this.bauaktenForm.get(controlName) as UntypedFormGroup;
    return Object.entries(group?.value || {})
        .filter(([_, checked]) => !!checked)
        .map(([shortName]) => shortName);
  }

  /**
   * Builds the result documents according to the current selection.
   * If 'Teile' is selected, creates a single pbauak document; otherwise builds
   * Bauakte/Statik or a combined document as needed.
   *
   * @param {string[]} selectedKeys Selected product short names.
   * @param {string} info Free-form information text.
   * @param {string} partDescription Description of the requested part (Teil).
   * @param {string} constructionYear Optional construction year.
   * @param {string[]} services Selected service short names to attach.
   * @return {Document[]} The list of constructed documents.
   */
  private buildResultDocuments(selectedKeys: string[], info: string, partDescription: string, constructionYear: string, services: string[]): Document[] {
    if (this.isBauakPartSelected() && this.pbauakKey) {
      // Special handling: only one pbauak document
      return [this.buildTeilDocument(info, partDescription, constructionYear, services)];
    }
    // Default handling: Bauakte, Statik, or combined bauakms
    return this.buildNonTeilDocuments(selectedKeys, info, constructionYear, services);
  }

  /**
   * Constructs a single pbauak (Teil) document filled with additional data,
   * services and selected subclasses.
   *
   * @param {string} info Free-form information text.
   * @param {string} partDescription Description of the requested part (Teil).
   * @param {string} constructionYear Optional construction year.
   * @param {string[]} services Selected service short names to attach.
   * @return {Document} The constructed pbauak document.
   */
  private buildTeilDocument(info: string, partDescription: string, constructionYear: string, services: string[]): Document {
    const doc = new Document(this.pbauakKey);
    doc.information = info; // legacy compatibility
    doc.partDescription = partDescription; // legacy compatibility

    // Populate additionalConstructionFileData
    const additionalConstructionFileData = new AdditionalConstructionFileData();
    additionalConstructionFileData.additionalInfo = info ?? null;
    additionalConstructionFileData.partDescription = partDescription ?? null;
    additionalConstructionFileData.constructionYear = constructionYear ?? null;
    doc.additionalConstructionFileData = additionalConstructionFileData;

    if (this.bauakDocumentClass) {
      doc.documentClass = this.bauakDocumentClass;
    }

    // Add services
    services.forEach(s => doc.selectedServices.push(new Service(s)));

    // Add subclasses (if any checked)
    const selectedSubs = this.extractCheckedKeys('bauakSubclasses');
    selectedSubs.forEach(shortName => {
      const subClass = this.bauakSubClasses.find(s => s.className === shortName);
      if (subClass) {
        const subInformation = new SubInformation();
        subInformation.subClass = subClass;
        subInformation.nameShort = subClass.className;
        subInformation.nameLong = subClass.name;
        subInformation.received = false;
        subInformation.requested = false;
        doc.subInformations.push(subInformation);
      }
    });

    return doc;
  }

  /**
   * Constructs one or more non-Teil documents: either a single combined
   * 'bauakms' document when both Bauakte and Statik are selected, or one
   * document per selected key otherwise.
   *
   * @param {string[]} selectedKeys Selected product short names.
   * @param {string} info Free-form information text.
   * @param {string} constructionYear Optional construction year.
   * @param {string[]} services Selected service short names to attach.
   * @return {Document[]} The constructed document list.
   */
  private buildNonTeilDocuments(selectedKeys: string[], info: string, constructionYear: string, services: string[]): Document[] {
    const nonTeilKeys = selectedKeys.filter(k => k !== this.pbauakKey);

    // Map to product metadata for detection
    const selectedProducts = nonTeilKeys
        .map(key => this.bauakProducts?.find(p => p.nameShort === key))
        .filter((p): p is Product => !!p);

    const hasStatik = selectedProducts.some(p => (p.nameShort || '').toLowerCase().includes('statik'));
    const hasBauakte = selectedProducts.some(p => (p.nameShort || '').toLowerCase().startsWith('bauak'));

    if (hasBauakte && hasStatik) {
      // Special case: combined document
      return [this.createDocument("bauakms", info, constructionYear, services)];
    }

    // Default: one document per selected key
    return nonTeilKeys.map(key => this.createDocument(key, info, constructionYear, services));
  }

  /**
   * Marks invalid inputs, shows validation feedback and scrolls the modal to
   * the top so users can see the errors.
   */
  private invalidateForm(): void {
    this.showInvalidInputs = true;
    FormUtils.markInvalidInputs(this.bauaktenForm);
    this.modalRef?.scrollToTop();
  }

  /**
   * Maps object owner form values into ObjectOwner DTOs.
   *
   * @return {ObjectOwner[]} The mapped list of object owners.
   */
  private mapObjectOwners(): ObjectOwner[] {
    return this.objectOwnerComponent?.objectOwners.controls.map(group => {
      const value = group.value;
      return {
        firstname: value.firstname?.trim() ?? '',
        lastname: value.lastname?.trim() ?? '',
        birthDate: value.birthDate ?? null,
        birthName: value.birthName?.trim() ?? '',
        company: value.company?.trim() ?? '',
        objectOwnerType: value.objectOwnerType
      } as ObjectOwner;
    }) ?? [];
  }

  /**
   * Persists folio number data into the selected property,
   * including folio creation/assignment and research flag.
   */
  private persistFolioData(): void {
    const formValues = this.folioNumberComponent?.folioNumberForm.get('numbers')?.value ?? [];

    try {
      LandregisterfolioUtils.createFoliosIfMissing(this.selectedProperty);
      LandregisterfolioUtils.assignFolioNumbers(this.selectedProperty.landregisterfolios, formValues);
    } catch (e) {
      console.error('Error while assigning folio numbers', e);
    }

    this.selectedProperty.dataResearch =
        this.folioNumberComponent?.folioNumberForm.get('dataResearch')?.value ?? false;
  }

  /**
   * Resolves the Grunde product (either 'grunde' or 'grundb') from all products.
   *
   * @return {Product|undefined} The Grunde product if available.
   */
  private get grundeProduct(): Product | undefined {
    return (this.products || []).find(p => ['grunde','grundb'].includes((p.nameShort||'').toLowerCase()));
  }
  /**
   * The short name of the resolved Grunde product.
   *
   * @return {string|undefined} The short name if available.
   */
  private get grundeShortName(): string | undefined {
    return this.grundeProduct?.nameShort;
  }
  /**
   * Indicates whether a Grunde document is already present in the cart.
   *
   * @return {boolean} True if an existing Grunde document is found.
   */
  private get hasExistingGrunde(): boolean {
    const names = (this.documents || []).map(d => (d?.docname || '').toLowerCase());
    return names.includes('grunde') || names.includes('grundb');
  }

  /**
   * Creates a new Document object with its associated metadata
   *
   * This helper ensures that every created document has:
   * - A `docname` (e.g., "bauakms", "bauak", "statik")
   * - An attached `AdditionalConstructionFileData` object populated
   *   with optional `info` and `constructionYear`
   * - A list of selected services mapped to `Service` objects
   *
   * @param docname Short name of the document type
   * @param info Optional additional information to attach
   * @param constructionYear Optional construction year metadata
   * @param services List of service identifiers to attach to the document
   * @return fully initialized document object
   */
  private createDocument(docname: string, info: string | null, constructionYear: string | null, services: string[]): Document {
    const doc = new Document(docname);

    // Attach additional construction file data
    const additionalConstructionFileData = new AdditionalConstructionFileData();
    additionalConstructionFileData.additionalInfo = info ?? null;
    additionalConstructionFileData.partDescription = null;
    additionalConstructionFileData.constructionYear = constructionYear ?? null;
    doc.additionalConstructionFileData = additionalConstructionFileData;

    // Add selected services
    services.forEach(s => doc.selectedServices.push(new Service(s)));

    return doc;
  }

  /**
   * Removes subclasses that contain the 'pbauak' class name
   *
   * @param subClasses Subclasses from where the 'pbauak' sub classes should be removed
   */
  private filterOutPbauakSubClasses(subClasses: SubClass[]): SubClass[]{
    const desiredOrder = ['BAUGE', 'FLAEBR', 'LPLAN', 'BAUAN', 'STPNACH', 'GRISS', 'BAUB', 'ABBESCH', 'ANSICH', 'BEBESCH', 'BRANDSCH', 'SCHNIT'];
    return subClasses
        // 1. Filter out unwanted subclasses
        .filter((subClass) => !subClass.className.toLowerCase().includes('pbauak'))
        // 2. Sort based on the desired order
        .sort((a,b) => {
          const indexA = desiredOrder.indexOf(a.className);
          const indexB = desiredOrder.indexOf(b.className);

          // Unlisted items go to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
  }
}
